import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Save, Eye, EyeOff, CheckCircle, XCircle, MessageCircle, Instagram, Upload, FileKey } from 'lucide-react';
import { instancesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const MetaApiConfig = ({ clienteId, instance, onConfigSaved }) => {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showManualConfig, setShowManualConfig] = useState(false);
  const [platform, setPlatform] = useState('whatsapp');
  const [publicKey, setPublicKey] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certStatus, setCertStatus] = useState(null);
  const [formData, setFormData] = useState({
    phone_number_id: '',
    instagram_account_id: '',
    access_token: '',
    business_account_id: '',
  });

  useEffect(() => {
    if (instance) {
      console.log('MetaApiConfig - Instance recebida:', instance);
      setPlatform(instance.platform || 'whatsapp');
      setFormData({
        phone_number_id: instance.phone_number_id || '',
        instagram_account_id: instance.instagram_account_id || '',
        access_token: instance.access_token || '',
        business_account_id: instance.business_account_id || '',
      });
      console.log('MetaApiConfig - FormData atualizado:', {
        phone_number_id: instance.phone_number_id,
        instagram_account_id: instance.instagram_account_id,
        access_token: instance.access_token ? '***' : '',
        business_account_id: instance.business_account_id,
      });
    }
  }, [instance, instance?.phone_number_id, instance?.instagram_account_id, instance?.access_token]);

  const handleSave = async () => {
    // Validação condicional por plataforma
    if (platform === 'whatsapp' && !formData.phone_number_id) {
      toast.error('Phone Number ID é obrigatório para WhatsApp');
      return;
    }
    if (platform === 'instagram' && !formData.instagram_account_id) {
      toast.error('Instagram Account ID é obrigatório');
      return;
    }
    if (!formData.access_token) {
      toast.error('Access Token é obrigatório');
      return;
    }

    setLoading(true);
    try {
      // Usar instance.id em vez de clienteId
      await instancesAPI.configureMetaApiById(instance.id, {
        ...formData,
        platform,
      });
      toast.success(`Configuração do ${platform === 'whatsapp' ? 'WhatsApp' : 'Instagram'} salva com sucesso!`);
      
      // Verificar conexão real após salvar
      toast.info('Verificando conexão com Meta API...');
      try {
        const verifyResponse = await instancesAPI.verifyConnection(instance.id);
        if (verifyResponse.data.connected) {
          const info = verifyResponse.data.phoneNumber || verifyResponse.data.displayName || 'Conectado';
          toast.success(`✅ Conexão verificada! ${info}`);
        } else {
          toast.error(`❌ Erro na conexão: ${verifyResponse.data.error}`);
        }
      } catch (verifyError) {
        console.error('Erro ao verificar conexão:', verifyError);
        toast.warning('Configuração salva, mas não foi possível verificar a conexão');
      }
      
      if (onConfigSaved) onConfigSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const isConfigured = platform === 'whatsapp' 
    ? instance?.phone_number_id && instance?.access_token
    : instance?.instagram_account_id && instance?.access_token;

  const handleVerifyConnection = async () => {
    if (!instance?.id) {
      toast.error('Instância não encontrada');
      return;
    }

    setVerifying(true);
    try {
      const response = await instancesAPI.verifyConnection(instance.id);
      if (response.data.connected) {
        toast.success(`✅ Conectado! ${response.data.phoneNumber || response.data.displayName}`);
      } else {
        toast.error(`❌ Desconectado: ${response.data.error}`);
      }
    } catch (error) {
      toast.error('Erro ao verificar conexão');
    } finally {
      setVerifying(false);
    }
  };

  const handleUploadCertificate = async () => {
    if (!publicKey.trim()) {
      toast.error('Cole a chave pública RSA');
      return;
    }

    if (!instance?.id) {
      toast.error('Instância não encontrada');
      return;
    }

    setUploadingCert(true);
    try {
      const response = await instancesAPI.uploadCertificate(instance.id, publicKey);
      toast.success('✅ Certificado enviado com sucesso!');
      if (response.data.s3_backup) {
        toast.info('📦 Backup salvo no S3');
      }
      setPublicKey('');
      await handleVerifyCertificate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar certificado');
    } finally {
      setUploadingCert(false);
    }
  };

  const handleVerifyCertificate = async () => {
    if (!instance?.id) return;

    try {
      const response = await instancesAPI.verifyCertificate(instance.id);
      setCertStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar certificado:', error);
    }
  };

  useEffect(() => {
    if (instance?.id && isConfigured && platform === 'whatsapp') {
      handleVerifyCertificate();
    }
  }, [instance?.id, isConfigured, platform]);

  const handleOAuthLogin = () => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const oauthUrl = `${baseUrl}/meta-auth/connect?cliente_id=${clienteId}&platform=${platform}`;
    window.location.href = oauthUrl;
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              platform === 'instagram' 
                ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                : 'bg-gradient-to-br from-green-500 to-green-600'
            }`}>
              {platform === 'instagram' ? (
                <Instagram className="w-6 h-6 text-white" />
              ) : (
                <MessageCircle className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Conectar {platform === 'instagram' ? 'Instagram' : 'WhatsApp'}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isConfigured ? 'Conectado' : 'Não configurado'}
              </p>
            </div>
          </div>
          {isConfigured ? (
            <CheckCircle className="w-8 h-8 text-green-500" />
          ) : (
            <XCircle className="w-8 h-8 text-gray-400" />
          )}
        </div>

        {/* Botão de Login OAuth */}
        <div className="space-y-4">
          <button
            onClick={handleOAuthLogin}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${
              platform === 'instagram'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isConfigured ? 'Reconectar' : 'Conectar'} com Meta
          </button>

          {/* Botão para alternar para config manual */}
          <button
            onClick={() => setShowManualConfig(!showManualConfig)}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
              darkMode
                ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            {showManualConfig ? 'Ocultar' : 'Configuração Manual'}
          </button>

          {/* Campos Manuais */}
          {showManualConfig && (
            <div className="space-y-4 pt-4 border-t border-gray-700">
              {platform === 'whatsapp' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phone Number ID
                  </label>
                  <input
                    type="text"
                    value={formData.phone_number_id}
                    onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Ex: 123456789012345"
                  />
                </div>
              )}

              {platform === 'instagram' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Instagram Account ID
                  </label>
                  <input
                    type="text"
                    value={formData.instagram_account_id}
                    onChange={(e) => setFormData({ ...formData, instagram_account_id: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Ex: 987654321098765"
                  />
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Business Account ID (WABA)
                </label>
                <input
                  type="text"
                  value={formData.business_account_id}
                  onChange={(e) => setFormData({ ...formData, business_account_id: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode
                      ? 'bg-zinc-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Ex: 123456789012345"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Access Token
                </label>
                <div className="relative">
                  <input
                    type={showAccessToken ? 'text' : 'password'}
                    value={formData.access_token}
                    onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                    className={`w-full px-3 py-2 pr-10 rounded-lg border ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Cole seu token aqui"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAccessToken(!showAccessToken)}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    {showAccessToken ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={loading}
                className={`w-full py-2 px-4 rounded-lg font-medium text-white transition-colors ${
                  platform === 'instagram'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50`}
              >
                <Save className="w-4 h-4 inline mr-2" />
                {loading ? 'Salvando...' : 'Salvar Configuração'}
              </button>
            </div>
          )}

          {isConfigured && (
            <>
              <button
                onClick={handleVerifyConnection}
                disabled={verifying}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  darkMode
                    ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                } disabled:opacity-50`}
              >
                {verifying ? 'Verificando...' : 'Verificar Conexão'}
              </button>

              {/* Seção de Certificado (apenas WhatsApp) */}
              {platform === 'whatsapp' && (
                <div className={`mt-6 p-4 rounded-lg border ${
                  darkMode ? 'bg-zinc-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <FileKey className="w-5 h-5 text-blue-500" />
                    <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Certificado RSA
                    </h4>
                  </div>

                  {certStatus && (
                    <div className={`mb-3 p-3 rounded-lg ${
                      certStatus.business_public_key_signature_status === 'VALID'
                        ? 'bg-green-500/10 border border-green-500/20'
                        : 'bg-yellow-500/10 border border-yellow-500/20'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {certStatus.business_public_key_signature_status === 'VALID' ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className={`text-sm font-medium ${
                          certStatus.business_public_key_signature_status === 'VALID'
                            ? 'text-green-500'
                            : 'text-yellow-500'
                        }`}>
                          Status: {certStatus.business_public_key_signature_status}
                        </span>
                      </div>
                    </div>
                  )}

                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Cole a chave pública RSA 2048-bit (arquivo public.pem)
                  </p>

                  <textarea
                    value={publicKey}
                    onChange={(e) => setPublicKey(e.target.value)}
                    placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
                    rows={6}
                    className={`w-full px-3 py-2 rounded-lg border font-mono text-xs ${
                      darkMode
                        ? 'bg-zinc-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />

                  <button
                    onClick={handleUploadCertificate}
                    disabled={uploadingCert || !publicKey.trim()}
                    className="w-full mt-3 py-2 px-4 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    {uploadingCert ? 'Enviando...' : 'Enviar Certificado'}
                  </button>

                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    💡 Gere as chaves com: <code className="bg-zinc-800 px-1 py-0.5 rounded">./gerar-certificado.sh</code>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaApiConfig;
